import React, { useContext, useMemo, useState } from "react";
import "./ExpandBranchesFaults.css";
import { Context } from "../../../context/Context";
import { translations } from "../../../utils/lang";
import { COMP_FIELDS2 } from "../../../utils/Compensation";
import { formatter } from "../../../utils/helpers";
import { ObjectionPanel } from "../objectionPanel/ObjectionPanel";
import { createObjectionAPI } from "../../../utils/api/objection";
import { Button } from "../../ui";
import { FaultSummaryCard } from "../BranchesFaults";

const MONTH_INDEX = {
  january: 0,
  february: 1,
  march: 2,
  april: 3,
  may: 4,
  june: 5,
  july: 6,
  august: 7,
  september: 8,
  october: 9,
  november: 10,
  december: 11,
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const toNumber = (value) => Number.parseFloat(value || 0) || 0;

function getMonthIndexFromKey(key) {
  if (!key) return Number.MAX_SAFE_INTEGER;

  const value = String(key).toLowerCase().trim();
  const monthMatch = value.match(
    /\b(january|jan|february|feb|march|mar|april|apr|may|june|jun|july|jul|august|aug|september|sept|sep|october|oct|november|nov|december|dec)\b/
  );

  if (monthMatch) return MONTH_INDEX[monthMatch[1]] ?? Number.MAX_SAFE_INTEGER;

  const tokens = value.split(/[^a-z]+/).filter(Boolean);
  for (const token of tokens) {
    if (Object.prototype.hasOwnProperty.call(MONTH_INDEX, token)) {
      return MONTH_INDEX[token];
    }
  }

  const parsed = new Date(key);
  if (!Number.isNaN(parsed.getTime())) return parsed.getMonth();

  return Number.MAX_SAFE_INTEGER;
}

function getMonthName(rawDate) {
  if (!rawDate) return "Unknown Month";

  const dateParts = String(rawDate).split("-");
  if (dateParts[1]) return dateParts[1];

  const dateObj = new Date(rawDate);
  if (!Number.isNaN(dateObj.getTime())) {
    return dateObj.toLocaleString("default", { month: "long" });
  }

  return "Unknown Month";
}

function createEmptySummary() {
  return {
    count: 0,
    objCount: 0,
    totalOrder: 0,
    totalEndurance: 0,
    totalRefund: 0,
  };
}

function addToSummary(summary, item, objectionSet) {
  const orderId = String(item[COMP_FIELDS2.orderId] || item.orderId || "");
  const refundAmount = toNumber(item[COMP_FIELDS2.refundAmount]);

  summary.count += 1;
  summary.objCount += objectionSet.has(orderId) || refundAmount > 0 ? 1 : 0;
  summary.totalOrder += toNumber(item[COMP_FIELDS2.orderAmount]);
  summary.totalEndurance += toNumber(item[COMP_FIELDS2.enduranceAmount]);
  summary.totalRefund += refundAmount;
}

function createOrderGroup(orderId, firstItem) {
  return {
    orderId,
    details: firstItem,
    items: [],
    summary: {
      totalOrder: 0,
      totalEndurance: 0,
      totalRefund: 0,
    },
  };
}

function addToOrderGroup(orderGroup, item) {
  orderGroup.items.push(item);
  orderGroup.summary.totalOrder += toNumber(item[COMP_FIELDS2.orderAmount]);
  orderGroup.summary.totalEndurance += toNumber(item[COMP_FIELDS2.enduranceAmount]);
  orderGroup.summary.totalRefund += toNumber(item[COMP_FIELDS2.refundAmount]);
}

export const ExpandBranchesFaults = ({ objections, data }) => {
  const { lang, handleTokenExpiration } = useContext(Context);
  const [expandedBranch, setExpandedBranch] = useState(null);
  const [expandedApp, setExpandedApp] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [objectionMonth, setObjectionMonth] = useState(null);
  const [loading, setLoading] = useState(false);

  const objectionSet = useMemo(() => new Set((objections || []).map(String)), [objections]);

  const groupedBranches = useMemo(() => {
    const branchMap = new Map();

    (data || []).forEach((item) => {
      const branchName = item[COMP_FIELDS2.branchName] || "Unknown Branch";
      const appName = item[COMP_FIELDS2.app] || "Unknown App";
      const monthName = getMonthName(item[COMP_FIELDS2.date]);
      const orderId = String(item[COMP_FIELDS2.orderId] || "Unknown Order");

      if (!branchMap.has(branchName)) {
        branchMap.set(branchName, {
          name: branchName,
          summary: createEmptySummary(),
          apps: new Map(),
        });
      }

      const branch = branchMap.get(branchName);
      addToSummary(branch.summary, item, objectionSet);

      if (!branch.apps.has(appName)) {
        branch.apps.set(appName, {
          name: appName,
          summary: createEmptySummary(),
          months: new Map(),
        });
      }

      const app = branch.apps.get(appName);
      addToSummary(app.summary, item, objectionSet);

      if (!app.months.has(monthName)) {
        app.months.set(monthName, {
          name: monthName,
          summary: createEmptySummary(),
          orders: new Map(),
          objectableOrders: new Map(),
        });
      }

      const month = app.months.get(monthName);
      addToSummary(month.summary, item, objectionSet);

      if (!month.orders.has(orderId)) {
        month.orders.set(orderId, createOrderGroup(orderId, item));
      }
      addToOrderGroup(month.orders.get(orderId), item);

      const isObjectable =
        !objectionSet.has(orderId) &&
        item[COMP_FIELDS2.orderStatus] !== "Rejected" &&
        toNumber(item[COMP_FIELDS2.refundAmount]) <= 0;

      if (isObjectable) {
        if (!month.objectableOrders.has(orderId)) {
          month.objectableOrders.set(orderId, []);
        }
        month.objectableOrders.get(orderId).push(item);
      }
    });

    return Array.from(branchMap.values()).map((branch) => ({
      ...branch,
      apps: Array.from(branch.apps.values()).map((app) => ({
        ...app,
        months: Array.from(app.months.values()).sort(
          (a, b) => getMonthIndexFromKey(a.name) - getMonthIndexFromKey(b.name)
        ),
      })),
    }));
  }, [data, objectionSet]);

  if (!data?.length) return null;

  const toggleBranch = (branch) => {
    setExpandedBranch(expandedBranch === branch ? null : branch);
    setExpandedApp(null);
    setExpandedMonth(null);
    setObjectionMonth(null);
  };

  const toggleApp = (app) => {
    setExpandedApp(expandedApp === app ? null : app);
    setExpandedMonth(null);
    setObjectionMonth(null);
  };

  const toggleMonth = (month) => {
    setExpandedMonth(expandedMonth === month ? null : month);
    setObjectionMonth(null);
  };

  const toggleObjection = (month) => {
    setObjectionMonth(objectionMonth === month ? null : month);
    setExpandedMonth(null);
  };

  const handleSubmitObjection = async (payload) => {
    if (!payload.length) {
      alert("Select at least one order");
      return;
    }

    const formData = new FormData();
    payload.forEach((order) => {
      const { img, ...orderData } = order;
      formData.append("orders", JSON.stringify(orderData));
      if (img) formData.append(`img[${order.orderId}]`, img);
    });

    try {
      const token = localStorage.getItem("auth-token");
      if (!token) {
        handleTokenExpiration();
        return;
      }

      setLoading(true);
      const objection = await createObjectionAPI(token, formData);
      // A failure is reported by createObjectionAPI (localized notice); the panel stays open to retry.
      if (objection) setObjectionMonth(null);
    } catch (error) {
      console.error("Fetching failed details:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="branchesFaults-card-col">
      {groupedBranches.map((branch) => (
        <div key={branch.name}>
          <FaultSummaryCard
            className="branch"
            expanded={expandedBranch === branch.name}
            onClick={() => toggleBranch(branch.name)}
            title={branch.name}
            count={`${branch.summary.objCount}/${branch.summary.count}`}
            totalOrder={branch.summary.totalOrder}
            totalEndurance={branch.summary.totalEndurance}
            totalRefund={branch.summary.totalRefund}
          />

          {expandedBranch === branch.name && (
            <div className="expand-apps">
              {branch.apps.map((app) => (
                <div key={app.name} className="expand-app-section">
                  <FaultSummaryCard
                    className="app"
                    expanded={expandedApp === app.name}
                    onClick={() => toggleApp(app.name)}
                    title={
                      translations[lang].filter.apps?.[app.name] ??
                      `${translations[lang].notApp} ${String(app.name).slice(-1)}`
                    }
                    count={`${app.summary.objCount}/${app.summary.count}`}
                    totalOrder={app.summary.totalOrder}
                    totalEndurance={app.summary.totalEndurance}
                    totalRefund={app.summary.totalRefund}
                  />

                  {expandedApp === app.name && (
                    <div className="expand-months">
                      {app.months.map((month) => {
                        const monthOrderIds = Array.from(month.orders.keys());
                        const allObjected = monthOrderIds.every((orderId) => objectionSet.has(orderId));
                        const objectableOrders = Object.fromEntries(month.objectableOrders);
                        const canObject = !allObjected && month.summary.totalRefund === 0;

                        return (
                          <div key={month.name} className="expand-month-section">
                            <FaultSummaryCard
                              className="month"
                              expanded={expandedMonth === month.name}
                              onClick={() => toggleMonth(month.name)}
                              title={translations[lang].filter.month[month.name] ?? month.name}
                              count={`${month.summary.objCount}/${month.summary.count}`}
                              totalOrder={month.summary.totalOrder}
                              totalEndurance={month.summary.totalEndurance}
                              totalRefund={month.summary.totalRefund}
                              action={
                                canObject ? (
                                  <Button
                                    variant="danger"
                                    appearance="filled"
                                    fullWidth
                                    className="branchesFaults-objection-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleObjection(month.name);
                                    }}
                                  >
                                    {translations[lang].objection.object}
                                  </Button>
                                ) : undefined
                              }
                            />

                            {expandedMonth === month.name && (
                              <div className="expand-orders">
                                {Array.from(month.orders.values()).map((order) => {
                                  const orderDetails = order.details;
                                  const isObjectedOrder = objectionSet.has(order.orderId);
                                  const isClosedOrder =
                                    order.summary.totalRefund > 0 ||
                                    orderDetails[COMP_FIELDS2.orderStatus] === "Rejected";

                                  return (
                                    <div key={order.orderId} className="branchesFaults-summary-card orderId">
                                      <div className="branchFaults-expand-card-col">
                                        <div
                                          className={`branchesFaults-p center orderId ${
                                            isObjectedOrder || isClosedOrder ? "objected" : ""
                                          }`}
                                        >
                                          <p>
                                            <strong>{order.orderId}</strong>
                                          </p>
                                        </div>
                                        <div className="branchesFaults-p">
                                          <p>
                                            {orderDetails[COMP_FIELDS2.date]} {orderDetails[COMP_FIELDS2.time]}
                                          </p>
                                        </div>
                                        <div className="branchesFaults-p">
                                          {translations[lang].filter.orderStatus[
                                            orderDetails[COMP_FIELDS2.orderStatus]
                                          ] ?? orderDetails[COMP_FIELDS2.orderStatus]}
                                        </div>
                                      </div>

                                      <div className="branchesFaults-p col">
                                        <strong>{translations[lang].reports.details.body[4]}</strong>{" "}
                                        {orderDetails[COMP_FIELDS2.cause]}
                                      </div>
                                      <div className="branchesFaults-p">
                                        {formatter.format(order.summary.totalOrder)}
                                      </div>
                                      <div className="branchesFaults-p">
                                        {formatter.format(order.summary.totalEndurance)}
                                      </div>
                                      <div className="branchesFaults-p">
                                        {formatter.format(order.summary.totalRefund)}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {objectionMonth === month.name && (
                              <ObjectionPanel
                                app={app.name}
                                month={month.name}
                                orders={objectableOrders}
                                loading={loading}
                                onCancel={() => setObjectionMonth(null)}
                                onSubmit={handleSubmitObjection}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
